import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import { IconUpload } from '@fabric-app/icons/svg';
import React__default, { useState } from 'react';
import { createPortal } from 'react-dom';
import { styled } from 'styled-components';
import { JSONTree } from 'react-json-tree';
import { Flex } from '@fabric-app/ui/components/Flex';
import { uploadStore } from '../upload.store.js';
import { useStore } from 'zustand';
import { useShallow } from '../../../node_modules/zustand/esm/react/shallow.js';
import { UploadTaskPriority, UploadTaskStatus } from '@fabric-app/types/upload';
import { P } from '@fabric-app/ui/components/Typography';
import Badge from '@fabric-app/ui/components/Badge';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { useThrottle } from '@fabric-app/hooks/useThrottle';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { ButtonIcon } from '@fabric-app/ui/components/Button';
import { CloseIcon } from '@fabric-app/icons/icons';

const minHeight = 500;
const UploadDevToolsButton = styled.button.withConfig({
  displayName: "UploadDevToolsBase__UploadDevToolsButton",
  componentId: "sc-826dd242-0"
})([
  `background:conic-gradient(from 0deg,#6fdcff,#a88cff,#f0faff,#8fffd3,#6fdcff);display:flex;align-items:center;justify-content:center;border-radius:100%;padding:6px;color:black;position:fixed;bottom:20px;left:20px;aspect-ratio:1/1;width:40px;height:40px;transition:transform 0.2s ease-in-out;box-shadow:inset 0 0 0 1px rgba(0,0,0,0.1);&:hover{transform:scale(1.1);}svg{width:26px;height:auto;mix-blend-mode:difference;filter:invert(1);}z-index:1000;&[data-open='true']{transform:translate(0,100px);}`
]);
const List = styled.table.withConfig({
  displayName: "UploadDevToolsBase__List",
  componentId: "sc-826dd242-1"
})([
  `position:relative;border-collapse:collapse;width:100%;color:`,
  `;overflow:auto;thead{position:sticky;top:0;background:`,
  `;text-align:left;border-bottom:1px solid `,
  `;}tbody{tr{cursor:pointer;&:hover{background:`,
  `;}&:not(:last-child){border-bottom:1px solid `,
  `;}}}`
], cssVar["color-text-primary"], cssVar["color-bg-tertiary"], cssVar["color-border-primary"], cssVar["color-bg-secondary"], cssVar["color-border-primary"]);
const ViewContainer = styled(Flex).attrs((p) => {
  return {
    gap: "element",
    direction: "column",
    ...p
  };
}).withConfig({
  displayName: "UploadDevToolsBase__ViewContainer",
  componentId: "sc-826dd242-2"
})([
  `background:`,
  `;padding:1rem;overflow:auto;`
], cssVar["color-bg-primary"]);
const Pane = styled(motion.div).withConfig({
  displayName: "UploadDevToolsBase__Pane",
  componentId: "sc-826dd242-3"
})([
  `position:fixed;bottom:0;left:0;right:0;background:`,
  `;display:flex;flex-direction:column;gap:0.5rem;pointer-events:auto !important;border-top:2px solid `,
  `;&[data-open='true']{min-height:`,
  `px;}z-index:1000;`
], cssVar["color-bg-primary"], cssVar["color-border-primary"], minHeight);
const PaneHeader = styled(Flex).attrs((p) => {
  return {
    gap: "element",
    justifyContent: "space-between",
    alignItems: "center",
    ...p
  };
}).withConfig({
  displayName: "UploadDevToolsBase__PaneHeader",
  componentId: "sc-826dd242-4"
})([
  `background:`,
  `;border-bottom:1px solid `,
  `;padding:0.5rem;`
], cssVar["color-bg-tertiary"], cssVar["color-border-primary"]);
const PaneContent = styled(Flex).attrs((p) => {
  return {
    gap: "element",
    direction: "column",
    ...p
  };
}).withConfig({
  displayName: "UploadDevToolsBase__PaneContent",
  componentId: "sc-826dd242-5"
})([
  `padding:0.5rem;`
]);
const ResizeHandle = styled(motion.div).withConfig({
  displayName: "UploadDevToolsBase__ResizeHandle",
  componentId: "sc-826dd242-6"
})([
  `position:absolute;top:-2px;left:0;right:0;height:2px;cursor:ns-resize;transition:background 0.2s ease-in-out;&:hover{background:`,
  `;}&::before{content:'';position:absolute;top:-5px;left:0;right:0;height:10px;}`
], cssVar["color-app-primary"]);
const UploadDevToolsPane = ({ setIsOpen }) => {
  const [height, setHeight] = useState(minHeight);
  const tasksStateSummary = useStore(uploadStore, useShallow((state) => state.tasks.reduce((acc, task) => {
    acc[task.status] = (acc[task.status] ?? 0) + 1;
    return acc;
  }, {})));
  const chunksStateSummary = useStore(uploadStore, useShallow((state) => state.tasks.reduce((acc, task) => {
    task.chunks.forEach((chunk) => {
      acc[chunk.status] = (acc[chunk.status] ?? 0) + 1;
    });
    return acc;
  }, {})));
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const { tasks } = useStore(uploadStore, useShallow((state) => ({
    tasks: state.tasks.map((t) => ({
      priority: t.priority,
      id: t.id,
      status: t.status,
      progress: t.progress,
      chunksPending: t.chunks.filter((c) => c.status === UploadTaskStatus.Pending).length,
      chunksUploading: t.chunks.filter((c) => c.status === UploadTaskStatus.Uploading).length,
      chunksCompleted: t.chunks.filter((c) => c.status === UploadTaskStatus.Completed).length,
      chunksFailed: t.chunks.filter((c) => c.status === UploadTaskStatus.Failed).length,
      chunkAmount: t.chunks.length
    }))
  })));
  const selectedTask = useStore(uploadStore, useShallow((state) => state.tasks.find((t) => t.id === selectedTaskId)));
  const throttledTask = useThrottle(selectedTask, 250);
  return /* @__PURE__ */ jsxs(Pane, {
    style: {
      height
    },
    children: [
      /* @__PURE__ */ jsx(ResizeHandle, {
        onPan: (_, info) => {
          setHeight((height2) => Math.max(minHeight, height2 - info.delta.y));
        }
      }),
      /* @__PURE__ */ jsxs(PaneHeader, {
        children: [
          /* @__PURE__ */ jsx(P, {
            size: "xs",
            weight: 700,
            children: "Upload DevTools"
          }),
          /* @__PURE__ */ jsx(ButtonIcon, {
            variant: "bg-secondary",
            onClick: () => setIsOpen(false),
            size: "xss",
            children: /* @__PURE__ */ jsx(CloseIcon, {
              style: {
                width: 12,
                height: 12
              }
            })
          })
        ]
      }),
      /* @__PURE__ */ jsx(ScrollArea.Default, {
        orientation: "both",
        asFlex: true,
        children: /* @__PURE__ */ jsxs(PaneContent, {
          gap: "text",
          direction: "column",
          children: [
            /* @__PURE__ */ jsx(P, {
              size: "xxs",
              children: "Tasks"
            }),
            /* @__PURE__ */ jsxs(Flex, {
              gap: "text",
              children: [
                " ",
                Object.entries(tasksStateSummary).map(([status, count]) => /* @__PURE__ */ jsxs(Badge.Static, {
                  variant: "bg-tertiary",
                  children: [
                    status,
                    "(",
                    count,
                    ")"
                  ]
                }, status))
              ]
            }),
            /* @__PURE__ */ jsx(P, {
              size: "xxs",
              children: "Chunks"
            }),
            /* @__PURE__ */ jsxs(Flex, {
              gap: "text",
              children: [
                " ",
                Object.entries(chunksStateSummary).map(([status, count]) => /* @__PURE__ */ jsxs(Badge.Static, {
                  variant: "bg-tertiary",
                  children: [
                    status,
                    "(",
                    count,
                    ")"
                  ]
                }, status))
              ]
            }),
            /* @__PURE__ */ jsxs(List, {
              children: [
                /* @__PURE__ */ jsxs("thead", {
                  children: [
                    /* @__PURE__ */ jsxs("tr", {
                      children: [
                        /* @__PURE__ */ jsx("th", {
                          colSpan: 4
                        }),
                        /* @__PURE__ */ jsx("th", {
                          colSpan: 5,
                          children: "Chunks"
                        })
                      ]
                    }),
                    /* @__PURE__ */ jsxs("tr", {
                      children: [
                        /* @__PURE__ */ jsx("th", {
                          children: "Priority"
                        }),
                        /* @__PURE__ */ jsx("th", {
                          children: "ID"
                        }),
                        /* @__PURE__ */ jsx("th", {
                          children: "Status"
                        }),
                        /* @__PURE__ */ jsx("th", {
                          children: "Progress"
                        }),
                        /* @__PURE__ */ jsx("th", {
                          children: "P"
                        }),
                        /* @__PURE__ */ jsx("th", {
                          children: "U"
                        }),
                        /* @__PURE__ */ jsx("th", {
                          children: "C"
                        }),
                        /* @__PURE__ */ jsx("th", {
                          children: "F"
                        }),
                        /* @__PURE__ */ jsx("th", {
                          children: "T"
                        })
                      ]
                    })
                  ]
                }),
                /* @__PURE__ */ jsx("tbody", {
                  children: tasks.map((task) => /* @__PURE__ */ jsxs("tr", {
                    onClick: () => setSelectedTaskId(task.id),
                    children: [
                      /* @__PURE__ */ jsx("td", {
                        children: UploadTaskPriority[task.priority]
                      }),
                      /* @__PURE__ */ jsx("td", {
                        children: task.id
                      }),
                      /* @__PURE__ */ jsx("td", {
                        children: task.status
                      }),
                      /* @__PURE__ */ jsxs("td", {
                        children: [
                          task.progress.toFixed(2),
                          "%"
                        ]
                      }),
                      /* @__PURE__ */ jsx("td", {
                        children: task.chunksPending
                      }),
                      /* @__PURE__ */ jsx("td", {
                        children: task.chunksUploading
                      }),
                      /* @__PURE__ */ jsx("td", {
                        children: task.chunksCompleted
                      }),
                      /* @__PURE__ */ jsx("td", {
                        children: task.chunksFailed
                      }),
                      /* @__PURE__ */ jsx("td", {
                        children: task.chunkAmount
                      })
                    ]
                  }, task.id))
                })
              ]
            }),
            /* @__PURE__ */ jsxs(ViewContainer, {
              children: [
                /* @__PURE__ */ jsx(JSONTree, {
                  data: throttledTask
                }),
                /* @__PURE__ */ jsx(P, {
                  children: "Events"
                }),
                /* @__PURE__ */ jsx(List, {
                  children: /* @__PURE__ */ jsx("tbody", {
                    children: throttledTask?.events.map((event, index) => /* @__PURE__ */ jsxs(React__default.Fragment, {
                      children: [
                        /* @__PURE__ */ jsxs("tr", {
                          children: [
                            /* @__PURE__ */ jsx("td", {
                              children: event.type
                            }),
                            /* @__PURE__ */ jsx("td", {
                              children: dayjs(event.timestamp).format("HH:mm:ss")
                            })
                          ]
                        }),
                        /* @__PURE__ */ jsx("tr", {
                          children: /* @__PURE__ */ jsx("td", {
                            colSpan: 2,
                            children: event.message
                          })
                        })
                      ]
                    }, index))
                  })
                })
              ]
            })
          ]
        })
      })
    ]
  }, "pane");
};
const UploadDevToolsBase = () => {
  const [isOpen, setIsOpen] = useState(false);
  if (typeof document === "undefined") {
    return null;
  }
  return /* @__PURE__ */ createPortal(/* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsx(UploadDevToolsButton, {
        "data-open": isOpen,
        onClick: () => setIsOpen(!isOpen),
        children: /* @__PURE__ */ jsx(IconUpload, {})
      }),
      isOpen && /* @__PURE__ */ jsx(UploadDevToolsPane, {
        setIsOpen
      }, "pane")
    ]
  }), getDefaultRadixPortalContainer() ?? document.body);
};

export { UploadDevToolsBase };
//# sourceMappingURL=UploadDevToolsBase.js.map
